import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Thread } from "@/components/messages/Thread";
import { Composer } from "@/components/messages/Composer";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

export function ChurchMessages() {
  const { settings, refreshUnread } = useApp();
  const [msgs, setMsgs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [to, setTo] = useState("");
  useEffect(() => { api.get("/messages/admins").then((r) => { setAdmins(r.data); setTo((t) => t || r.data[0]?.id || ""); }).catch(() => {}); }, []);
  const load = useCallback(() => api.get("/messages/thread").then((r) => { setMsgs(r.data); refreshUnread(); })
    .catch((e) => toast.error(errMsg(e), { id: "msg-load-error" })), [refreshUnread]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const send = async (body, attachments) => {
    await api.post("/messages", { body, attachments, user_id: to || null });
    toast.success(`Message sent to ${admins.find((a) => a.id === to)?.label || "the admin team"}`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message for everyone?")) { return; }
    try { await api.delete(`/messages/${id}`); toast.success("Message deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="church-messages">
      <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-white"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <div className="font-heading font-bold text-slate-900">MWJK Admins</div>
          <div className="text-xs text-slate-500">Your messages go only to the MWJK admin team</div>
        </div>
      </div>
      <Thread messages={msgs} mineIsAdmin={false} onDelete={remove} onChanged={load} empty="No messages yet. Choose an admin and write your message below." />
      {settings.messaging?.church_can_send !== false ? (
        <>
          {admins.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 pt-4 text-sm" data-testid="church-send-to">
              <span className="font-semibold text-slate-700">Send to:</span>
              {admins.map((a) => (
                <button key={a.id} type="button" onClick={() => setTo(a.id)} data-testid={`church-recipient-${a.id}`}
                  className={`rounded-full border px-4 py-1.5 font-semibold transition-colors ${to === a.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}>{a.label}</button>
              ))}
            </div>
          )}
          <Composer onSend={send} placeholder={`Write a message to ${admins.find((a) => a.id === to)?.label || "the admin team"}...`} testid="church-composer" />
        </>
      )
        : <p className="border-t border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid="church-send-off">Sending messages is turned off by the Master Admin. You can still read messages here.</p>}
    </div>
  );
}
